'use client';
import { useState, useEffect, useRef } from 'react';
import { useCampaign } from '@/app/CampaignContext';
import { MODAL_TYPES, useModal } from './ModalProvider';
import { elevarAddToCart } from '@/lib/elevar/cart-events';
import { gtmAddToCart } from '@/utils/events/gtmEvents';
import { useCart } from '@/components/commerce/cart/cart-context';
import { useRouter } from 'next/navigation';
import { SvgCheck } from '../product/Icons';
import { SingleCard } from '../product/AtcInterstitial/SingleCard';
import { Slider } from '../product/AtcInterstitial/Slider';
import { heapTrack } from '@/lib/heap/api';

export default function CrossSellInterstitialWrapper({
  isOpen,
  onClose,
  state,
  settings
}) {
  const modalRef = useRef(null);
  const { modalUtils, getCloseButton } = useModal();
  const campaignContext = useCampaign();
  const router = useRouter();
  const { addCartItems } = useCart();
  const interData = settings?.atc_interstitial;
  const [cardData, setCardData] = useState();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const hideInterstitial = (e) => {
    const togglerElement = e.target.closest('[modal-toggler="true"]');
    if (togglerElement) {
      e.preventDefault();
      setIsLoading(false);
      onClose();
      router.push('/cart');
    }
  };

  useEffect(() => {
    if (isOpen && modalRef?.current) {
      modalRef?.current?.focus();
      // Ensure modal content starts at the top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Ensure modal scrolls to top when opened
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Find the modal container with overflow-auto
      const modalContainer = modalRef.current.closest(
        '.overflow-auto, .overflow-y-auto'
      );
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
      // Also ensure the modal body itself is at top
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
        heapTrack('Leesa Modal Closed', {
          'Leesa - Modal Type': MODAL_TYPES.crossSellInterstitial,
          'Leesa - Modal Close Method': 'Escape Key'
        });
        router.push('/cart');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, router]);

  const addSelectedProducts = (product, variant, selected) => {
    const tempSelectedProducts = selectedProducts;
    const variantObj = {
      product: product,
      variant: variant,
      customAttributes:
        [
          { key: '_productType', value: product?.productType },
          { key: '_productTitle', value: product?.title },
          { key: '_productImage', value: variant?.image?.url }
        ] || []
    };
    if (!selected) {
      tempSelectedProducts.push(variantObj);
      setSelectedProducts([...tempSelectedProducts]);
    } else {
      setSelectedProducts(
        tempSelectedProducts.filter((item) => item?.product?.id !== product?.id)
      );
    }
  };

  const handleAdd = async () => {
    setIsLoading(true);
    const tempSelectedProducts = selectedProducts;
    if (cardData?.length < 2) {
      tempSelectedProducts.push({
        product: cardData?.[0]?.product,
        variant: cardData?.[0]?.variant,
        customAttributes:
          [
            { key: '_productType', value: cardData?.[0]?.product?.productType },
            { key: '_productTitle', value: cardData?.[0]?.product?.title },
            { key: '_productImage', value: cardData?.[0]?.variant?.image?.url }
          ] || []
      });
    }

    if (tempSelectedProducts?.length) {
      try {
        const payload = tempSelectedProducts.map((item) => {
          const customAttributes = [
            { key: '_productType', value: item?.product?.productType },
            { key: '_productTitle', value: item?.product?.title },
            { key: '_productImage', value: item?.variant?.image?.url }
          ];
          return {
            variant: item?.variant,
            product: item?.product,
            quantity: 1,
            customAttributes
          };
        });
        const { error } = await addCartItems(payload);

        // The cart context already showed the error toast. Leave the modal open
        // so the shopper can retry, but let go of the loading state.
        if (error) {
          setIsLoading(false);
          return;
        }

        elevarAddToCart(payload);
        gtmAddToCart({ itemsToAdd: payload, page: 'Page' });
      } catch (error) {
        console.error('Error adding items to cart:', error);
      }
    }
    onClose();
    setIsLoading(false);
    router.push('/cart');
  };

  useEffect(() => {
    const sizeToMatch = state?.variant?.selectedOptions?.find(
      (option) => option?.name == 'Size'
    )?.value;

    const tempCardVariants = [];

    interData?.products?.forEach((card) => {
      const matchingVariant = findMatchingVariant(
        card?.product?.variants,
        sizeToMatch,
        card?.secondary_attribute,
        card?.product?.productType
      );
      if (matchingVariant) {
        tempCardVariants.push({
          ...card,
          variant: matchingVariant
        });
      }
    });

    setCardData(tempCardVariants);
  }, [state?.variant, interData?.products]);

  if (!isOpen || !cardData) return null;

  return modalUtils.createModalWrapper({
    modalType: MODAL_TYPES.crossSellInterstitial,
    isOpen,
    onClose: () => {
      onClose();
      setIsLoading(false);
      router.push('/cart');
    },
    containerClassName: modalUtils.containerPresets.crossSell.greenInterstitial,
    children: (
      <div ref={modalRef} tabIndex={-1}>
        <div className="top-3 right-3">
          {getCloseButton(MODAL_TYPES.crossSellInterstitial, () => {
            setIsLoading(false);
            onClose();
            router.push('/cart');
          })}
        </div>
        <div className="mb-3 flex">
          <SvgCheck />
          {state?.variant?.productTitle && (
            <div className="sh13 px-1 text-white">
              {state?.variant?.productTitle}
            </div>
          )}
          <div className="text-light-green body-xs self-center">
            Added to Cart
          </div>
        </div>
        <div className="mb-8">
          {interData?.heading && (
            <h2 className="h6 sm:h5 mb-1 text-white">{interData?.heading}</h2>
          )}
          {interData?.subheading && (
            <div className="body-s sm:body-r text-dawn-grey-60">
              {interData?.subheading}
            </div>
          )}
        </div>

        {cardData?.length > 1 ? (
          <Slider
            cards={cardData}
            discountType={campaignContext?.discount_tag?.discount_type}
            addSelectedProducts={addSelectedProducts}
            selectedProducts={selectedProducts}
          />
        ) : (
          <SingleCard card={cardData?.[0]} />
        )}

        <div className="flex w-full flex-col sm:flex-row">
          {interData?.atc_button_text && (
            <button
              className={`fit-content btn btn-primary mb-2.5 grow cursor-pointer sm:mr-2 sm:mb-0 ${
                isLoading ? 'opacity-60' : ''
              }`}
              data-cy="add-interstitial-button"
              onClick={handleAdd}
              disabled={isLoading}
            >
              {isLoading ? 'Adding' : interData?.atc_button_text}
            </button>
          )}
          {interData?.decline_button_text && (
            <button
              className="btn btn-tertiary-light grow cursor-pointer"
              modal-toggler="true"
              data-cy="decline-interstitial-button"
              onClick={(e) => hideInterstitial(e)}
            >
              {interData?.decline_button_text}
            </button>
          )}
        </div>
      </div>
    )
  });
}

function findMatchingVariant(variants, size, secondaryAttribute, type) {
  const kingSizes = ['King', 'Cal King'];

  const matchingVariant = variants?.find((variant) => {
    function findKingSizeOption(variant) {
      return (
        variant?.selectedOptions?.find((option) => option?.name == 'Size')
          ?.value == 'King'
      );
    }
    function findNotKingSizeOption(variant) {
      return (
        variant?.selectedOptions?.find((option) => option?.name == 'Size')
          ?.value !== 'King'
      );
    }

    function findSecondaryOption(variant) {
      return (
        variant?.selectedOptions?.find((option) => option?.name !== 'Size')
          ?.value == secondaryAttribute
      );
    }

    function findMatchingSizeOption(variant) {
      return (
        variant?.selectedOptions?.find((option) => option?.name == 'Size')
          ?.value == size
      );
    }

    if (type == 'Pillow' && secondaryAttribute) {
      if (kingSizes.includes(size)) {
        return findKingSizeOption(variant) && findSecondaryOption(variant);
      } else {
        return findNotKingSizeOption(variant) && findSecondaryOption(variant);
      }
    } else if (type == 'Pillow' && !secondaryAttribute) {
      if (kingSizes.includes(size)) {
        return findKingSizeOption(variant);
      } else {
        return findNotKingSizeOption(variant);
      }
    } else if (secondaryAttribute) {
      return findMatchingSizeOption(variant) && findSecondaryOption(variant);
    } else {
      return findMatchingSizeOption(variant);
    }
  });
  return matchingVariant;
}
